import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Users,
  CalendarDays,
  Target,
  LogOut,
  Save,
  Download,
  QrCode,
  Search,
  Percent,
  FileText,
  Printer,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { api, formatApiErrorDetail, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-muted-foreground text-sm">
        <Icon className="w-4 h-4" /> {label}
      </div>
      <div className="font-heading text-3xl font-extrabold mt-2">{value}</div>
    </div>
  );
}

export default function AdminDashboard() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [petition, setPetition] = useState(null);
  const [signatures, setSignatures] = useState([]);
  const [form, setForm] = useState({ title: "", subtitle: "", body: "", goal: 1000 });
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");

  const load = async () => {
    try {
      const [p, s] = await Promise.all([api.get("/petition/admin"), api.get("/signatures")]);
      setPetition(p.data);
      setForm({
        title: p.data.title,
        subtitle: p.data.subtitle || "",
        body: p.data.body,
        goal: p.data.goal,
      });
      setSignatures(s.data);
    } catch (e) {
      toast.error("Errore nel caricamento dati");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/petition", { ...form, goal: Number(form.goal) });
      toast.success("Petizione aggiornata");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const doLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const openDocument = (path) => {
    const token = localStorage.getItem("token");
    window.open(`${API}/documents/${path}?auth=${encodeURIComponent(token)}`, "_blank");
  };

  const printList = () => window.print();

  const exportCsv = () => {
    const rows = [
      ["Nome", "Cognome", "Comune", "Email", "Documento", "Data"],
      ...signatures.map((s) => [
        s.first_name,
        s.last_name,
        s.city || "",
        s.email || "",
        s.document_path ? "S\u00ec" : "No",
        new Date(s.created_at).toLocaleString("it-IT"),
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "firme-petizione.csv";
    a.click();
    toast.success("CSV esportato");
  };

  if (!petition)
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        Caricamento...
      </div>
    );

  const filtered = signatures.filter((s) =>
    `${s.first_name} ${s.last_name} ${s.city || ""} ${s.email || ""}`.toLowerCase().includes(query.toLowerCase())
  );
  const conversion = petition.goal
    ? Math.round((petition.total_signatures / petition.goal) * 100)
    : 0;

  return (
    <div className="min-h-screen bg-background">
      <div className="no-print-area">
      <header className="border-b border-border bg-card">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <span className="font-heading font-extrabold text-lg">Dashboard Organizzatore</span>
          <div className="flex items-center gap-2">
            <Link to="/qr">
              <Button variant="outline" size="sm" className="rounded-xl gap-1.5" data-testid="dashboard-qr-button">
                <QrCode className="w-4 h-4" /> QR Code
              </Button>
            </Link>
            <Button variant="ghost" size="sm" className="gap-1.5" onClick={doLogout} data-testid="logout-button">
              <LogOut className="w-4 h-4" /> Esci
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat icon={Users} label="Firme totali" value={petition.total_signatures} testid="stat-total" />
          <Stat icon={CalendarDays} label="Firme oggi" value={petition.today_signatures} testid="stat-today" />
          <Stat icon={Target} label="Obiettivo" value={petition.goal} testid="stat-goal" />
          <Stat icon={Percent} label="Completamento" value={`${conversion}%`} testid="stat-conversion" />
        </div>

        {/* Editor */}
        <form onSubmit={save} className="rounded-2xl border border-border bg-card p-6 sm:p-8" data-testid="petition-editor">
          <h2 className="font-heading text-xl font-bold mb-5">Modifica petizione</h2>
          <div className="space-y-4">
            <div>
              <Label htmlFor="title">Titolo</Label>
              <Input
                id="title"
                data-testid="edit-title-input"
                className="mt-1.5 rounded-xl"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="subtitle">Sottotitolo</Label>
              <Input
                id="subtitle"
                data-testid="edit-subtitle-input"
                className="mt-1.5 rounded-xl"
                value={form.subtitle}
                onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="body">Testo della petizione</Label>
              <Textarea
                id="body"
                data-testid="edit-body-input"
                className="mt-1.5 rounded-xl min-h-[220px]"
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                required
              />
            </div>
            <div className="max-w-xs">
              <Label htmlFor="goal">Obiettivo firme</Label>
              <Input
                id="goal"
                type="number"
                min="1"
                data-testid="edit-goal-input"
                className="mt-1.5 rounded-xl"
                value={form.goal}
                onChange={(e) => setForm({ ...form, goal: e.target.value })}
                required
              />
            </div>
          </div>
          <Button type="submit" disabled={saving} className="rounded-xl mt-6 gap-2" data-testid="save-petition-button">
            <Save className="w-4 h-4" /> {saving ? "Salvataggio..." : "Salva modifiche"}
          </Button>
        </form>

        {/* Signatures table */}
        <div className="rounded-2xl border border-border bg-card p-6 sm:p-8">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <h2 className="font-heading text-xl font-bold">Firmatari ({signatures.length})</h2>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  data-testid="search-signatures-input"
                  placeholder="Cerca..."
                  className="pl-9 rounded-xl w-48"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>
              <Button variant="outline" className="rounded-xl gap-1.5" onClick={printList} data-testid="print-list-button">
                <Printer className="w-4 h-4" /> Stampa
              </Button>
              <Button variant="outline" className="rounded-xl gap-1.5" onClick={exportCsv} data-testid="export-csv-button">
                <Download className="w-4 h-4" /> CSV
              </Button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <Table data-testid="admin-signatures-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Cognome</TableHead>
                  <TableHead>Comune</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Documento</TableHead>
                  <TableHead>Data</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                      Nessuna firma trovata
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((s) => (
                    <TableRow key={s.id} data-testid="signature-row">
                      <TableCell className="font-medium">{s.first_name}</TableCell>
                      <TableCell>{s.last_name}</TableCell>
                      <TableCell>{s.city || "\u2014"}</TableCell>
                      <TableCell className="text-muted-foreground">{s.email || "\u2014"}</TableCell>
                      <TableCell>
                        {s.document_path ? (
                          <Button
                            variant="outline"
                            size="sm"
                            className="rounded-lg gap-1.5 h-8"
                            data-testid="view-document-button"
                            onClick={() => openDocument(s.document_path)}
                          >
                            <FileText className="w-3.5 h-3.5" /> Vedi
                          </Button>
                        ) : (
                          <span className="text-muted-foreground">{"\u2014"}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {new Date(s.created_at).toLocaleString("it-IT")}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </main>
      </div>

      {/* Printable report (visible only when printing) */}
      <div className="print-report" data-testid="print-report">
        <h1>{petition.title}</h1>
        {petition.subtitle && <p className="pr-sub">{petition.subtitle}</p>}
        <p className="pr-meta">
          Elenco firme raccolte — Totale: {signatures.length} — Stampato il{" "}
          {new Date().toLocaleString("it-IT")}
        </p>
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Nome</th>
              <th>Cognome</th>
              <th>Comune</th>
              <th>Email</th>
              <th>Doc.</th>
              <th>Data</th>
            </tr>
          </thead>
          <tbody>
            {signatures.map((s, i) => (
              <tr key={s.id}>
                <td>{i + 1}</td>
                <td>{s.first_name}</td>
                <td>{s.last_name}</td>
                <td>{s.city || "-"}</td>
                <td>{s.email || "-"}</td>
                <td>{s.document_path ? "Sì" : "-"}</td>
                <td>{new Date(s.created_at).toLocaleString("it-IT")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <style>{`
        .print-report { display: none; }
        @media print {
          @page { size: A4; margin: 14mm; }
          .no-print-area { display: none !important; }
          .print-report { display: block !important; color: #000; }
          .print-report h1 { font-size: 20px; margin: 0 0 4px; }
          .print-report .pr-sub { font-size: 13px; color: #333; margin: 0 0 8px; }
          .print-report .pr-meta { font-size: 12px; color: #555; margin: 0 0 14px; border-bottom: 1px solid #000; padding-bottom: 8px; }
          .print-report table { width: 100%; border-collapse: collapse; font-size: 11px; }
          .print-report th, .print-report td { border: 1px solid #999; padding: 5px 7px; text-align: left; vertical-align: top; }
          .print-report th { background: #f0f0f0; }
          .print-report tr { break-inside: avoid; }
        }
      `}</style>
    </div>
  );
}
